// Stand-in for the admin disputes endpoint until it exists (see
// docs/temp/admin-disputes-backend-tz.md). Rows carry exactly the fields the contract
// names, and `queryAdminDisputes` applies the same query parameters the real endpoint
// will, so filters, sorting and pagination are exercised for real against it.
//
// Delete this file together with the mock branches in services.js once the endpoint lands.
import dayjs from "dayjs"

import { FLAG_REASON_META } from "../../DisputesPage/pages/AllPage/constants/allPageData"

const ACCOUNTS = [
  {
    business_account_id: 10,
    business_account: "Dropship",
    business_website: "https://dropship.io",
    user_full_name: "Marcus Reyes",
    user_email: "marcus@dropship.io",
    is_test_account: false
  },
  {
    business_account_id: 11,
    business_account: "Beta Store",
    business_website: "https://betastore.com",
    user_full_name: "Hannah Voss",
    user_email: "hannah@betastore.com",
    is_test_account: false
  },
  {
    business_account_id: 12,
    business_account: "Northwind Supply",
    business_website: "https://northwindsupply.co",
    user_full_name: "Priya Nair",
    user_email: "priya@northwindsupply.co",
    is_test_account: false
  },
  {
    business_account_id: 13,
    business_account: "Lumen Labs",
    business_website: "https://lumenlabs.dev",
    user_full_name: "Tomas Eriksen",
    user_email: "tomas@lumenlabs.dev",
    is_test_account: true
  }
]

const MEMBERS = [
  { id: "member-1", name: "David Rossi", email: "david.rossi@dropship.io" },
  { id: "member-2", name: "Sophie Khan", email: "sophie.khan@dropship.io" },
  { id: "member-3", name: "Liam Silva", email: "liam.silva@dropship.io" },
  { id: "member-4", name: "Emma Anderson", email: "emma.anderson@dropship.io" },
  null
]

const REASONS = [
  {
    reason_code: "10.4",
    reason_code_label: "Other Fraud, Card-Absent Environment.",
    reason_category: "fraudulent",
    reason_category_label: "Fraudulent"
  },
  {
    reason_code: "13.1",
    reason_code_label: "Merchandise / Services Not Received.",
    reason_category: "product_not_received",
    reason_category_label: "Product not received"
  },
  {
    reason_code: "13.2",
    reason_code_label: "Canceled Recurring Transaction.",
    reason_category: "subscription_canceled",
    reason_category_label: "Subscription canceled"
  },
  {
    reason_code: "13.3",
    reason_code_label: "Not as Described or Defective Merchandise / Services.",
    reason_category: "product_unacceptable",
    reason_category_label: "Product unacceptable"
  },
  {
    reason_code: "12.6",
    reason_code_label: "Duplicate Processing.",
    reason_category: "duplicate",
    reason_category_label: "Duplicate"
  },
  {
    reason_code: "13.6",
    reason_code_label: "Credit Not Processed.",
    reason_category: "credit_not_processed",
    reason_category_label: "Credit not processed"
  }
]

const CARDS = [
  { issuer: "visa", issuing_bank: "Chase", card_type: "debit", card_last4: "4242" },
  {
    issuer: "mastercard",
    issuing_bank: "Bank of America",
    card_type: "credit",
    card_last4: "5301"
  },
  { issuer: "amex", issuing_bank: "Capital One", card_type: "credit", card_last4: "3782" },
  { issuer: "discover", issuing_bank: "Citi", card_type: "credit", card_last4: "6011" }
]

const CUSTOMERS = [
  ["Olivia Bennett", "olivia.bennett@example.com"],
  ["Liam Carter", "liam.carter@example.com"],
  ["Sophia Nguyen", "sophia.nguyen@example.com"],
  ["Noah Mitchell", "noah.mitchell@example.com"],
  ["Emma Davis", "emma.davis@example.com"],
  ["James Wilson", "james.wilson@example.com"],
  ["Ava Thompson", "ava.thompson@example.com"],
  ["Benjamin Lee", "benjamin.lee@example.com"]
]

const STATUSES = [
  "needs_response",
  "needs_response",
  "awaiting_review",
  "pending_decision",
  "won",
  "won",
  "lost_defended",
  "lost_undefended",
  "escalated",
  "skipped"
]

const PROCESSORS = ["stripe", "shopify"]
const COUNTRIES = ["US", "GB", "CA", "AU", "DE", "FR", "NL"]
const CURRENCIES = { US: "USD", GB: "GBP", CA: "CAD", AU: "AUD", DE: "EUR", FR: "EUR", NL: "EUR" }
// Only the reasons that actually render as a flag (FLAG_REASON_META entries with meta).
const FLAGS = [
  null,
  null,
  null,
  "repeat_disputer",
  "amount_above_limit",
  "below_win_likelihood",
  "reason_code_always_manual"
]

const ROW_COUNT = 64
const at = (list, i) => list[i % list.length]

// A win is billable; a quarter of them are still owed. Everything else has no fee at all,
// which the row carries as null rather than a zero that would read as "nothing to pay".
const feeFor = (status, amount, i) => {
  if (status !== "won") return { paid_status: null, paid_amount: null }
  if (i % 4 === 0) return { paid_status: "unpaid", paid_amount: null }
  return { paid_status: "paid", paid_amount: Number((amount * 0.25).toFixed(2)) }
}

const buildPool = () => {
  const now = dayjs("2026-09-18T11:20:49")

  return Array.from({ length: ROW_COUNT }, (_, i) => {
    const account = at(ACCOUNTS, i)
    const reason = at(REASONS, i)
    const card = at(CARDS, i)
    const [customer_full_name, customer_email] = at(CUSTOMERS, i)
    const status = at(STATUSES, i)
    const country = at(COUNTRIES, i)
    const amount = 39 + ((i * 37) % 420)
    const created = now.subtract(i, "day").subtract(i % 7, "hour")

    return {
      id: `demo-${String(i + 1).padStart(4, "0")}`,
      number: ROW_COUNT - i,
      status,
      dispute_type: i % 9 === 0 ? "inquiry" : "dispute",
      processor: at(PROCESSORS, i),
      processor_dispute_id: `du_DEMO${String(i + 1).padStart(3, "0")}${card.card_last4}`,
      ...reason,
      ...card,
      amount: amount.toFixed(2),
      currency: CURRENCIES[country],
      customer_full_name,
      customer_email,
      country,
      dispute_created_at: created.toISOString(),
      updated_at: created.add(1, "day").toISOString(),
      due_by: created.add(7, "day").toISOString(),
      flag_reason: at(FLAGS, i),
      predates_connection: i % 17 === 0,
      enrichment: (i * 13) % 101,
      assigned_to: at(MEMBERS, i),
      // A production-account row, so the pinned case is visible in the default Test Off view.
      pinned: i === 5,
      ...feeFor(status, amount, i),
      ...account
    }
  })
}

let pool = null
const getPool = () => {
  if (!pool) pool = buildPool()
  return pool
}

export const setMockPin = (id, pin) => {
  getPool().forEach((row) => {
    if (row.id === id) row.pinned = !!pin
  })
}

const asList = (value) => {
  if (value == null || value === "all") return []
  return Array.isArray(value) ? value : String(value).split(",").filter(Boolean)
}

const terms = (value) =>
  asList(value)
    .map((v) => String(v).trim().toLowerCase())
    .filter(Boolean)

const matchesTerms = (list, value) =>
  !list.length ||
  list.some((term) =>
    String(value || "")
      .toLowerCase()
      .includes(term)
  )

const matchesNumber = (value, type, min, max) => {
  if (!type || (min == null && max == null)) return true
  const n = Number(value) || 0
  const a = min == null ? null : Number(min)
  const b = max == null ? null : Number(max)
  switch (type) {
    case "is_equal_to":
      return a == null || n === a
    case "is_greater_than":
      return a == null || n > a
    case "is_less_than":
      return a == null || n < a
    case "is_between":
      return (a == null || n >= a) && (b == null || n <= b)
    default:
      return true
  }
}

const inRange = (value, from, to) => {
  if (!from && !to) return true
  const d = dayjs(value)
  if (!d.isValid()) return false
  if (from && d.isBefore(dayjs(from).startOf("day"))) return false
  if (to && d.isAfter(dayjs(to).endOf("day"))) return false
  return true
}

// Submission is an event type read off the status, not a field of its own — the same
// derivation the merchant list uses.
const submissionOf = (row) => {
  if (["skipped", "lost_undefended"].includes(row.status)) return "canceled"
  if (row.status === "needs_response") return "scheduled"
  if (row.status === "awaiting_review") return "needs_review"
  if (["pending_decision", "won", "lost_defended", "escalated"].includes(row.status)) return "sent"
  return null
}

// A flag only counts when the registry actually renders it — `ok` and `auto_approve_disabled`
// carry no meta and are not per-dispute reasons.
const flagIdOf = (row) => (FLAG_REASON_META[row.flag_reason] ? row.flag_reason : null)

const SORT_VALUE = {
  number: (r) => Number(r.number) || 0,
  status: (r) => r.status || "",
  amount: (r) => Number(r.amount) || 0,
  paid_amount: (r) => Number(r.paid_amount) || 0,
  paid_status: (r) => r.paid_status || "",
  dispute_created_at: (r) => dayjs(r.dispute_created_at).valueOf(),
  updated_at: (r) => dayjs(r.updated_at).valueOf(),
  due_by: (r) => dayjs(r.due_by).valueOf()
}

// Default order, the merchant list's own: pinned first, then the two statuses that need
// somebody, by deadline, then everything else newest first.
const OPEN_STATUSES = ["needs_response", "awaiting_review"]
const byPriority = (a, b) => {
  if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1
  const aOpen = OPEN_STATUSES.includes(a.status)
  const bOpen = OPEN_STATUSES.includes(b.status)
  if (aOpen !== bOpen) return aOpen ? -1 : 1
  if (aOpen && bOpen) return dayjs(a.due_by).valueOf() - dayjs(b.due_by).valueOf()
  return dayjs(b.dispute_created_at).valueOf() - dayjs(a.dispute_created_at).valueOf()
}

export const queryAdminDisputes = (params = {}) => {
  const {
    page = 1,
    page_size = 50,
    ordering = "priority",
    test_data = "false",
    amount_type,
    amount_min,
    amount_max,
    enrichment_type,
    enrichment_min,
    enrichment_max,
    date_from,
    date_to,
    updated_from,
    updated_to
  } = params

  const inList = (key, value) => {
    const list = asList(params[key])
    return !list.length || list.includes(value)
  }

  const filtered = getPool().filter((row) => {
    if (!!row.is_test_account !== (String(test_data) === "true")) return false

    if (!inList("status", row.status)) return false
    if (!inList("dispute_type", row.dispute_type)) return false
    if (!inList("reason_category", row.reason_category)) return false
    if (!inList("reason_code", row.reason_code)) return false
    if (!inList("issuer", row.issuer)) return false
    if (!inList("issuing_bank", row.issuing_bank)) return false
    if (!inList("card_type", row.card_type)) return false
    if (!inList("currency", row.currency)) return false
    if (!inList("country", row.country)) return false
    if (!inList("processor", row.processor)) return false
    if (!inList("submission", submissionOf(row))) return false
    if (!inList("paid_status", row.paid_status || "not_billable")) return false
    if (!inList("assigned_to", row.assigned_to?.id)) return false

    if (!inList("flag_reason", flagIdOf(row))) return false

    if (!matchesTerms(terms(params.customer), `${row.customer_full_name} ${row.customer_email}`))
      return false
    if (!matchesTerms(terms(params.user_full_name), row.user_full_name)) return false
    if (!matchesTerms(terms(params.user_email), row.user_email)) return false
    if (!matchesTerms(terms(params.business_account), row.business_account)) return false
    if (!matchesTerms(terms(params.business_url), row.business_website)) return false
    if (!matchesTerms(terms(params.card_last4), row.card_last4)) return false
    if (!matchesTerms(terms(params.processor_dispute_id), row.processor_dispute_id)) return false

    if (!matchesNumber(row.amount, amount_type, amount_min, amount_max)) return false
    if (!matchesNumber(row.enrichment, enrichment_type, enrichment_min, enrichment_max))
      return false
    if (!inRange(row.dispute_created_at, date_from, date_to)) return false
    if (!inRange(row.updated_at, updated_from, updated_to)) return false

    return true
  })

  let sorted
  if (ordering === "priority" || !ordering) {
    sorted = [...filtered].sort(byPriority)
  } else {
    const desc = String(ordering).startsWith("-")
    const valueOf = SORT_VALUE[desc ? ordering.slice(1) : ordering]
    sorted = valueOf
      ? [...filtered].sort((a, b) => {
          const va = valueOf(a)
          const vb = valueOf(b)
          if (va === vb) return 0
          return (va > vb ? 1 : -1) * (desc ? -1 : 1)
        })
      : filtered
  }

  const start = (page - 1) * page_size
  return { count: sorted.length, results: sorted.slice(start, start + page_size) }
}
